// One Company switcher for every page (owner and account manager).
// Presentation only: the server still enforces company access.

import { clear, h } from "../dom.js";
import {
  encodeScopeValue,
  orderedSwitcherGroups,
  parseScopeValue,
  switcherShowsGroups,
  type HomeScope,
} from "../scope.js";
import type { Company, CompanySlug, DashboardGroup, StaffRole } from "../types.js";
import * as face from "./face.js";

export type SwitcherMode = "home" | "company" | "fleet";

export interface PaintCompanySwitcherOptions {
  slot: HTMLElement;
  role: StaffRole;
  mode: SwitcherMode;
  scope: HomeScope;
  /** Active company on company-scoped surfaces. */
  company: CompanySlug;
  groups: readonly DashboardGroup[];
  companies: readonly Company[];
  /**
   * Home: changing scope. Company: changing company. Fleet: a company choice
   * opens that company's Home.
   */
  onChange: (next: HomeScope | { kind: "company"; slug: CompanySlug } | "new-group") => void;
}

/** The one directory for the rows this control was given. */
function directoryOf(companies: readonly Company[]) {
  return face.createCompanyDirectory(
    companies.map((row) => ({ slug: row.slug, displayName: row.displayName })),
    { complete: true },
  );
}

/** A company as a person reads it: its display name, else "this company". */
function companyLabel(company: Company, dir: ReturnType<typeof directoryOf>): string {
  return face.company(company.slug, dir, "sentence");
}

/**
 * The owner's personal-books control: "Company" is the only label. One
 * company is plain text. Two or more are one dropdown; All companies and saved
 * groups are extra entries under a "Groups" heading when the owner has saved a
 * group or has more than three companies.
 */
export function paintCompanySwitcher(opts: PaintCompanySwitcherOptions): void {
  const { slot, role, mode, scope, company, groups, companies } = opts;
  const dir = directoryOf(companies);
  clear(slot);

  if (companies.length === 0) return;

  if (companies.length === 1) {
    const only = companies[0];
    slot.append(
      h("div", { class: "company-picker-label", "data-role": "company-single" }, [
        h("span", { class: "company-picker-caption" }, ["Company"]),
        h("span", {
          class: "company-single-name",
          "data-role": "company-single-name",
        }, [only === undefined ? face.company(company, dir, "heading") : companyLabel(only, dir)]),
      ]),
    );
    return;
  }

  const select = h("select", {
    class: "company-select scope-select",
    "aria-label": "Company switcher",
    "data-role": role === "owner" ? "scope-switcher" : "company-picker",
  });

  for (const row of companies) {
    select.append(
      h("option", { value: encodeScopeValue({ kind: "company", slug: row.slug }) }, [
        companyLabel(row, dir),
      ]),
    );
  }

  if (role === "owner" && switcherShowsGroups(groups, companies.length)) {
    const groupHost = h("optgroup", { label: "Groups" });
    groupHost.append(h("option", { value: "all" }, ["All companies"]));
    const { defaultGroup, others } = orderedSwitcherGroups(groups);
    if (defaultGroup !== null) {
      groupHost.append(
        h("option", { value: encodeScopeValue({ kind: "group", groupId: defaultGroup.id }) }, [
          defaultGroup.name,
        ]),
      );
    }
    for (const group of others) {
      groupHost.append(
        h("option", { value: encodeScopeValue({ kind: "group", groupId: group.id }) }, [
          group.name,
        ]),
      );
    }
    groupHost.append(h("option", { value: "new-group" }, ["New group…"]));
    select.append(groupHost);
  }

  const preferred =
    mode === "company"
      ? encodeScopeValue({ kind: "company", slug: company })
      : mode === "home"
        ? encodeScopeValue(scope)
        : scope.kind === "all" || scope.kind === "group"
          ? encodeScopeValue(scope)
          : encodeScopeValue({ kind: "company", slug: company });
  select.value = preferred;
  if (select.value === "") {
    // The selected group/all entry is not shown for this owner (for example a
    // stale saved group). Fall back to the company, never to a blank control.
    select.value = encodeScopeValue({ kind: "company", slug: company });
  }

  select.addEventListener("change", () => {
    const parsed = parseScopeValue(select.value);
    if (parsed === null) {
      select.value = preferred;
      return;
    }
    if (parsed === "new-group") {
      opts.onChange("new-group");
      return;
    }
    opts.onChange(parsed);
  });

  slot.append(
    h("label", { class: "company-picker-label" }, [
      h("span", { class: "company-picker-caption" }, ["Company"]),
      select,
    ]),
  );
}
